import { Box, Grid } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import BranchFilterSearch from 'new-components/select/branch-filter-search'
import DoctorFee from 'features/dashboard/Dashboard/doctorFee'
import HightSaleProduct from 'features/dashboard/Dashboard/hightSaleProduct'
import LowSaleProduct from 'features/dashboard/Dashboard/lowSaleProduct'
import PaymentMethod from 'features/dashboard/Dashboard/paymentMethod'
import SalesOperative from 'features/dashboard/Dashboard/salesOperative'
import SalesProduct from 'features/dashboard/Dashboard/salesProduct'
import Summary from 'features/dashboard/Dashboard/summary'
import TotalExpenses from 'features/dashboard/Dashboard/totalExpenses'
import HightSaleOperative from './Dashboard/hightSaleOperative'
import OperativeType from './Dashboard/operativeType'

/** CONSTANTS */
import { KEY_STORAGE } from 'constants/common'
import { getSecureItem, setSecureItem } from 'utils/app.utils'

/** API */
import ClinicApi, { BranchInterface } from 'api/master/clinic.api'

/** STYLE */
import * as UseStyled from 'features/dashboard/useStyled'

export interface CardSalesProps {
  branches?: BranchInterface[]
  branch?: (string | number)[]
  rangeDate?: string[]
  handleChangeDashboard?: (key: string, value: any) => void
}

export default function Dashboard() {
  const { t } = useTranslation()

  const dashboardMain = getSecureItem(KEY_STORAGE.DASHBOARD)
  const dashboardCurrent = dashboardMain ? JSON.parse(dashboardMain) : {}

  const [dataDashboard, setDataDashboard] = useState<any>(dashboardCurrent || {})

  const [branches, setBranches] = useState<BranchInterface[]>([])
  const [branch, setBranch] = useState<(string | number)[]>(['ALL'])
  const [rangeDate, setRangeDate] = useState([moment().format('YYYY-MM-01'), moment().format('YYYY-MM-DD')])

  const handleChangeDashboard = (key: string, value: any) => {
    const [firstKey, secKey] = key?.split(',')
    if (firstKey === "branch" || firstKey === "rangeDate") {
      const updatedData = { ...dataDashboard };
      if (firstKey === "branch") {
        Object.keys(updatedData).forEach(k => {
          if (updatedData[k].branch) {
            updatedData[k].branch = value;
          }
        });
      }
      if (firstKey === "rangeDate") {
        Object.keys(updatedData).forEach(k => {
          if (updatedData[k].rangeDate) {
            updatedData[k].rangeDate = value;
          }
        });
      }

      setDataDashboard({ ...updatedData, [firstKey]: value });
    } else if (!secKey) {
      setDataDashboard({ ...dataDashboard, [firstKey]: value });
    } else {
      setDataDashboard({
        ...dataDashboard,
        [firstKey]: { ...dataDashboard[firstKey], [secKey]: value },
      });
    }
  }

  useEffect(() => {
    setSecureItem(KEY_STORAGE.DASHBOARD, JSON.stringify(dataDashboard))
  }, [dataDashboard])

  useEffect(() => {
    setBranch(dataDashboard?.branch || ['ALL'])
    setRangeDate(dataDashboard?.rangeDate || [moment().format('YYYY-MM-01'), moment().format('YYYY-MM-DD')])
  }, [dataDashboard?.branch, dataDashboard?.rangeDate])

  const loadDefaultData = async () => {
    await ClinicApi.findAllBranches().then(({ data }) => setBranches(data))
  }

  const handleChangeSelectBranch = (e: any) => {
    handleChangeDashboard("branch", handleChangeFilterSelect(e))
    // setBranch(handleChangeFilterSelect(e))
  }

  useEffect(() => {
    loadDefaultData()
  }, [])

  return (
    <div className="p-3 px-md-4">
      <Grid container spacing={2} className="mb-4">
        <Grid item xs={12} sm={4} md={4} lg={3} xl={2} className="w-100">
          <BranchFilterSearch
            branches={branches}
            value={branch}
            onchange={handleChangeSelectBranch}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={3} xl={2} className="w-100">
          <InputSecondNewRangePicker
            inputHeight={40}
            value={rangeDate}
            onchange={(val) => {
              if (_.isEmpty(val)) {
                handleChangeDashboard("rangeDate", emptyRangePicker)
                // setRangeDate(emptyRangePicker)
              }
              else {
                handleChangeDashboard("rangeDate", [moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                //  setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }
            }}
            allowClear
            onClear={() => handleChangeDashboard("rangeDate", emptyRangePicker)}
          />
        </Grid>
      </Grid>
      <Summary branch={branch} rangeDate={rangeDate} />
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.SALE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <SalesProduct branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <SalesOperative branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <DoctorFee rangeDate={rangeDate} branch={branch} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item lg={6} className="w-100 mb-4">
          <Box className="d-flex flex-column h-100">
            <UseStyled.Title type="header">{t('DASHBOARD.HEADER.TOTAL_EXPENSES')}</UseStyled.Title>
            <Box className="pt-3 h-100">
              <TotalExpenses branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
            </Box>
          </Box>
        </Grid>
        <Grid item lg={6} className="w-100 mb-4">
          <Box className="d-flex flex-column h-100">
            <UseStyled.Title type="header">{t('DASHBOARD.HEADER.PAYMENT')}</UseStyled.Title>
            <Box className="pt-3 h-100">
              <PaymentMethod branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.OPERATIVE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <OperativeType branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <HightSaleOperative branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
      <Grid container spacing={2} className="mb-4">
        <Grid item className="w-100">
          <UseStyled.Title type="header">{t('DASHBOARD.HEADER.WAREHOUSE')}</UseStyled.Title>
        </Grid>
        <Grid item lg={6} className="w-100">
          <LowSaleProduct branches={branches} branch={branch} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
        <Grid item lg={6} className="w-100">
          <HightSaleProduct branches={branches} branch={branch} rangeDate={rangeDate} handleChangeDashboard={handleChangeDashboard} />
        </Grid>
      </Grid>
    </div>
  )
}

export const handleChangeFilterSelect = (e: any) => {
  const value = e.target.value
  if (_.last(value) === 'ALL' || _.isEmpty(value)) return ['ALL']
  else
    return _.filter(value, (v) => {
      return !_.includes(v, 'ALL')
    })
}

export const renderFilterSelect = (type: 'branch' | 'dentist' | 'item' | 'operativeType', data: any, value: (string | number)[]) => {
  if (_.last(value) === 'ALL') return 'ALL'
  else {
    const items: string[] = []
    _.map(value, (v) => {
      if (type === 'branch') items.push(_.get(_.find(data, { branchId: v }), 'branchName'))
      else if (type === 'dentist') items.push(_.get(_.find(data, { dentistId: v }), 'dentistFullname'))
      else if (type === 'operativeType') items.push(_.get(_.find(data, { itemTypeId: v }), 'itemTypeName'))
      else items.push(_.get(_.find(data, { operativeId: v }), 'operativeName'))
    })
    return items.toString().replaceAll(',', ', ')
  }
}
